import { Box, ThemeProvider, alpha, lighten, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Sidebar from './Sidebar';
import Header from './Header';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { ThemeContext } from 'src/theme/ThemeProvider';
import { runMainTour } from 'src/utils/mainTour';
import { useContext, useEffect, useState } from 'react';

const SidebarLayout = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  const { desktopSidebarOpen } = useContext(SidebarContext);
  const { darkTheme } = useContext(ThemeContext);

  // Check if current language is Arabic
  const isRTL = i18n.language === 'ar' || theme.direction === 'rtl';

  // Safe cookie handling
  const getCookie = (name) => {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(name + '='));
    return cookie ? cookie.split('=')[1] : null;
  };

  const firstVisit = getCookie('firstVisit') || 'false';
  const nbVisit = getCookie('nbVisit') || '0';

  useEffect(() => {
    if (firstVisit !== 'true' || nbVisit !== '0') return;

    runMainTour(i18n);

    document.cookie = 'firstVisit=false; path=/; expires=Thu, 01 Jan 2030 00:00:00 GMT';
    document.cookie = 'nbVisit=1; path=/; expires=Thu, 01 Jan 2030 00:00:00 GMT';
  }, []);

  return (
    <>
      <Box
        sx={{
          flex: 1,
          height: '100%',
          '.MuiPageTitle-wrapper': {
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.trueWhite[5]
                : theme.colors.alpha.white[50],
            marginBottom: `${theme.spacing(4)}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 1px 0 ${alpha(
                  lighten(theme.colors.primary.main, 0.7),
                  0.15
                )}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
                : `0px 2px 4px -3px ${alpha(
                  theme.colors.alpha.black[100],
                  0.1
                )}, 0px 5px 12px -4px ${alpha(
                  theme.colors.alpha.black[100],
                  0.05
                )}`
          }
        }}
      >
        {/* L'en-tête reste sombre dans tous les modes, avec le logo d'origine. */}
        <ThemeProvider theme={darkTheme}>
          <Header />
        </ThemeProvider>
        <Sidebar />
        <Box
          sx={{
            position: 'relative',
            zIndex: 5,
            display: 'block',
            flex: 1,
            pt: `${theme.header.height}`,
            transition: theme.transitions.create(
              ['margin-left', 'margin-right'],
              {
                duration: theme.transitions.duration.standard
              }
            ),
            [theme.breakpoints.up('lg')]: {
              // For RTL, sidebar is on RIGHT, so push content LEFT
              // For LTR, sidebar is on LEFT, so push content RIGHT
              // No offset when the sidebar is hidden on desktop
              ...(isRTL
                ? {
                  marginRight: desktopSidebarOpen
                    ? `${theme.sidebar.width}`
                    : 0,
                  marginLeft: 0
                }
                : {
                  marginLeft: desktopSidebarOpen
                    ? `${theme.sidebar.width}`
                    : 0,
                  marginRight: 0
                })
            }
          }}
        >
          <Box display="block">
            <Outlet />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SidebarLayout;
